import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapTrifold, MapPin, FunnelSimple, Warning } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import GeocodeResolverPanel from "../components/crm/GeocodeResolverPanel";
import LocationFixModal from "../components/crm/LocationFixModal";

const ALL = "__ALL__";
const TOP_N = 5;

const FILTER_KEYS = ["agent_id", "buyer_persona", "status", "country", "lead_source"];

export default function LeadMap() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [enums, setEnums] = useState({ buyer_personas: [], lead_sources: [], lead_statuses: [], countries: [] });
  const [filters, setFilters] = useState({
    agent_id: ALL, buyer_persona: ALL, status: ALL, country: ALL, lead_source: ALL,
  });
  const [selected, setSelected] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [showAllCities, setShowAllCities] = useState(false);
  const [fixLead, setFixLead] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/users").then((r) => r.data),
      api.get("/meta/enums").then((r) => r.data),
    ]).then(([users, e]) => {
      setAgents(users.filter((u) => u.role === "agent"));
      setEnums({
        buyer_personas: e.buyer_personas || [],
        lead_sources: e.lead_sources || [],
        lead_statuses: e.lead_statuses || [],
        countries: e.countries || [],
      });
    });
  }, []);

  const reloadMap = () => {
    setLoading(true);
    const params = {};
    FILTER_KEYS.forEach((k) => { if (filters[k] && filters[k] !== ALL) params[k] = filters[k]; });
    return api.get("/leads/map", { params })
      .then(({ data }) => { setData(data); setSelected(null); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const totals = data?.totals || { leads_mapped: 0, cities_covered: 0, countries_covered: 0, missing_location_count: 0 };

  const FilterSelect = ({ k, label, options }) => (
    <div className="w-44">
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</label>
      <Select value={filters[k]} onValueChange={(v) => setFilter(k, v)}>
        <SelectTrigger data-testid={`map-filter-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value || o} value={o.value || o}>
              {o.label || o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="lead-map-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Geography</div>
          <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <MapTrifold size={30} weight="duotone" className="text-[#1A3A21]" /> Lead Map
          </h1>
          <p className="text-sm text-gray-500 mt-1">Commercial coverage by city. Click a city to view its leads.</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <FunnelSimple size={14} /> Filters
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterSelect k="agent_id" label="Agent"
            options={agents.map((a) => ({ value: a.id, label: a.name || a.email }))} />
          <FilterSelect k="buyer_persona" label="Persona" options={enums.buyer_personas} />
          <FilterSelect k="status" label="Status" options={enums.lead_statuses} />
          <FilterSelect k="country" label="Country" options={enums.countries} />
          <FilterSelect k="lead_source" label="Source" options={enums.lead_sources} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-gray-200 rounded-md p-4 bg-white" data-testid="map-total-mapped">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Mapped Leads</div>
          <div className="font-display text-3xl font-bold mt-1">{totals.leads_mapped}</div>
        </div>
        <div className="border border-gray-200 rounded-md p-4 bg-white" data-testid="map-cities">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Cities Covered</div>
          <div className="font-display text-3xl font-bold mt-1">{totals.cities_covered}</div>
        </div>
        <div className="border border-gray-200 rounded-md p-4 bg-white" data-testid="map-countries">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">Countries Covered</div>
          <div className="font-display text-3xl font-bold mt-1">{totals.countries_covered}</div>
        </div>
        <button
          onClick={() => setShowMissing((s) => !s)}
          className="border border-yellow-200 bg-yellow-50 hover:bg-yellow-100 rounded-md p-4 text-left transition"
          data-testid="map-missing">
          <div className="text-xs uppercase tracking-wider text-yellow-700 font-medium flex items-center gap-1">
            <Warning weight="bold" size={12} /> Missing or Unmapped
          </div>
          <div className="font-display text-3xl font-bold mt-1 text-yellow-900">{totals.missing_location_count}</div>
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="map-leaflet-wrap">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-display font-bold tracking-tight text-sm">City Heatmap</h3>
          <div className="text-[11px] text-gray-400">Circle size reflects lead count · click a marker to drill in</div>
        </div>
        <div style={{ height: 380 }}>
          {data?.cities?.length ? (
            <MapContainer
              center={[48.5, 10]}
              zoom={4}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
              key={data.cities.length /* re-init when filters change result count */}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {data.cities.map((c) => {
                const radius = Math.min(40, 6 + Math.sqrt(c.lead_count) * 3);
                const active = selected?.city === c.city && selected?.country === c.country;
                return (
                  <CircleMarker
                    key={`${c.city}-${c.country}`}
                    center={[c.latitude, c.longitude]}
                    radius={radius}
                    pathOptions={{
                      color: active ? "#E1A104" : "#1A3A21",
                      fillColor: active ? "#E1A104" : "#1A3A21",
                      fillOpacity: 0.55,
                      weight: active ? 3 : 1.5,
                    }}
                    eventHandlers={{ click: () => setSelected(c) }}
                  >
                    <LeafletTooltip direction="top" offset={[0, -2]} opacity={0.95}>
                      <strong>{c.city}</strong>, {c.country} — {c.lead_count} lead{c.lead_count === 1 ? "" : "s"}
                    </LeafletTooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">
              {loading ? "Loading map..." : "No cities to map with the current filters."}
            </div>
          )}
        </div>
      </div>

      {/* DEPLOY-2: mobile-only selected-city panel directly under the map and
          above City Distribution. Desktop keeps the right-column panel below. */}
      <div className="lg:hidden bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="map-detail-panel-mobile">
        {selected ? (
          <>
            <div className="px-5 py-3 border-b border-gray-200 bg-[#1A3A21] text-white">
              <div className="text-[10px] uppercase tracking-wider opacity-75">{selected.country}</div>
              <div className="font-display text-lg font-bold">{selected.city}</div>
              <div className="text-xs">{selected.lead_count} lead{selected.lead_count === 1 ? "" : "s"}</div>
            </div>
            <ul className="divide-y divide-gray-100 max-h-[24rem] overflow-y-auto">
              {selected.leads.map((l) => (
                <li key={l.id} className="px-5 py-3 hover:bg-gray-50">
                  <Link to={`/leads/${l.id}`} className="block" data-testid={`map-lead-mobile-${l.id}`}>
                    <div className="font-medium text-sm text-[#1A3A21]">{l.company_name}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                      <span>{l.lead_status}</span>
                      <span>·</span>
                      <span>{l.buyer_persona}</span>
                      {l.assigned_agent && <><span>·</span><span>{l.assigned_agent}</span></>}
                      {l.lead_source && <><span>·</span><span>{l.lead_source}</span></>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="p-6 text-center text-sm text-gray-400">
            Click a city in the map or table below to see its leads here.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="map-leaflet-wrap-OLD-REMOVED">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-display font-bold tracking-tight text-sm">City Distribution</h3>
            <div className="flex items-center gap-3">
              <div className="text-[11px] text-gray-400 hidden sm:block">Aggregated city groups</div>
              {(data?.cities?.length || 0) > TOP_N && (
                <button
                  type="button"
                  onClick={() => setShowAllCities((s) => !s)}
                  data-testid="toggle-show-all-cities"
                  className="text-[11px] font-semibold text-[#1A3A21] hover:underline"
                >
                  {showAllCities ? "Show less" : `Show all cities (${data.cities.length})`}
                </button>
              )}
            </div>
          </div>
          <div
            className={`overflow-auto ${showAllCities ? "max-h-[28rem]" : ""}`}
            data-testid="map-cities-table"
          >
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500">City</th>
                  <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Country</th>
                  <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Leads</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="py-8 text-center text-gray-500">Loading...</td></tr>
                ) : data?.cities?.length ? (
                  [...data.cities]
                    .sort((a, b) => (b.lead_count - a.lead_count) || a.city.localeCompare(b.city))
                    .slice(0, showAllCities ? data.cities.length : TOP_N)
                    .map((c) => (
                  <tr key={`${c.city}-${c.country}`}
                    onClick={() => setSelected(c)}
                    data-testid={`map-city-row-${c.city}`}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50 ${selected?.city === c.city ? "bg-yellow-50" : ""}`}>
                    <td className="py-2.5 px-4 font-medium flex items-center gap-2">
                      <MapPin size={13} weight="fill" className="text-[#1A3A21]" />
                      {c.city}
                    </td>
                    <td className="py-2.5 px-4 text-gray-600">{c.country}</td>
                    <td className="py-2.5 px-4 text-right">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-[#1A3A21] text-white text-xs font-semibold">
                        {c.lead_count}
                      </span>
                    </td>
                  </tr>
                ))
                ) : (
                  <tr><td colSpan={3} className="py-8 text-center text-gray-500">No cities to show with the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="map-detail-panel">
          {selected ? (
            <>
              <div className="px-5 py-3 border-b border-gray-200 bg-[#1A3A21] text-white">
                <div className="text-[10px] uppercase tracking-wider opacity-75">{selected.country}</div>
                <div className="font-display text-lg font-bold">{selected.city}</div>
                <div className="text-xs">{selected.lead_count} lead{selected.lead_count === 1 ? "" : "s"}</div>
              </div>
              <ul className="divide-y divide-gray-100 max-h-[28rem] overflow-y-auto">
                {selected.leads.map((l) => (
                  <li key={l.id} className="px-5 py-3 hover:bg-gray-50">
                    <Link to={`/leads/${l.id}`} className="block" data-testid={`map-lead-${l.id}`}>
                      <div className="font-medium text-sm text-[#1A3A21]">{l.company_name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{l.lead_status}</span>
                        <span>·</span>
                        <span>{l.buyer_persona}</span>
                        {l.assigned_agent && <><span>·</span><span>{l.assigned_agent}</span></>}
                        {l.lead_source && <><span>·</span><span>{l.lead_source}</span></>}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="p-8 text-center text-sm text-gray-400">
              Click a city in the table to see its leads here.
            </div>
          )}
        </div>
      </div>

      {user?.role === "admin" && (
        <GeocodeResolverPanel
          compact
          onResolved={reloadMap}
          contextLabel="Preview unmapped city/country pairs, then geocode valid locations using the backend cache. Lead Map will refresh automatically."
        />
      )}

      {showMissing && data?.missing_location?.leads?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5" data-testid="map-missing-list">
          <h3 className="font-display font-bold text-sm mb-2 flex items-center gap-2 text-yellow-900">
            <Warning weight="bold" /> Missing or Unmapped Location ({data.missing_location.count})
          </h3>
          <p className="text-[11px] text-yellow-800 mb-3">
            These leads are missing city/country data or their city is not yet mapped.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.missing_location.leads.map((l) => (
              <button key={l.id} type="button"
                onClick={() => setFixLead(l)}
                data-testid={`map-missing-lead-${l.id}`}
                className="text-left text-xs bg-white rounded border border-yellow-200 px-3 py-2 hover:border-yellow-400 hover:bg-yellow-100/40 transition">
                <div className="font-medium text-[#1A3A21]">{l.company_name}</div>
                <div className="text-gray-500">{l.city || "—"}, {l.country || "—"}</div>
                <div className="text-[10px] text-yellow-700 mt-0.5">
                  {l.classification === "missing" ? "Missing data" : "Unmapped — click to fix"}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <LocationFixModal
        open={!!fixLead}
        lead={fixLead}
        onOpenChange={(o) => { if (!o) setFixLead(null); }}
        onSaved={() => { setFixLead(null); reloadMap(); }}
      />
    </div>
  );
}
